import React from "react";

const DesignSystem = () => {
  // Read colors from CSS variables to ensure theme consistency
  const colors = {
    primary: "var(--color-primary, #226f54)",
    secondary: "var(--color-secondary, #b01f2c)",
    accent: "var(--color-accent, #43291f)",
    background: "var(--color-background, #F0F0F0)",
    text: "var(--color-text, #F0F0F0)",
    alert: "var(--color-alert, #b01f2c)",
  };

  const gradients = [
    "from-primary to-secondary",
    "from-secondary to-accent",
    "from-primary via-accent to-secondary",
    "from-accent to-primary",
  ];

  const buttonVariants = ["primary", "secondary", "accent", "alert"];
  const textSizes = ["xs", "sm", "base", "lg", "xl", "2xl", "3xl", "4xl"];

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold mb-12">Ciao Fridays Design System</h1>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Colors</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(colors).map(([name, value]) => (
            <div key={name} className="space-y-2">
              <div className={`h-24 rounded-lg shadow-md bg-${name}`}></div>
              <div className="text-sm">
                <p className="font-semibold capitalize">{name}</p>
                <p className="font-mono text-gray-600">{value}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Gradients</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {gradients.map((gradient, index) => (
            <div key={index} className="space-y-2">
              <div
                className={`h-24 rounded-lg shadow-md bg-gradient-to-r ${gradient}`}
              ></div>
              <code className="text-sm font-mono text-gray-600">
                bg-gradient-to-r {gradient}
              </code>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Typography</h2>
        <div className="space-y-4">
          {textSizes.map((size) => (
            <div key={size} className="flex items-center gap-4">
              <div className="w-20 text-sm text-gray-600">text-{size}</div>
              <p className={`text-${size}`}>
                The quick brown fox jumps over the lazy dog
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Buttons</h2>
        <div className="flex flex-wrap gap-4">
          {buttonVariants.map((variant) => (
            <React.Fragment key={variant}>
              <button
                className={`px-4 py-2 rounded-md bg-${variant} text-white hover:bg-opacity-90 transition-colors`}
              >
                Button {variant}
              </button>
              <button
                className={`px-4 py-2 rounded-md border-2 border-${variant} text-${variant} hover:bg-${variant} hover:bg-opacity-10 transition-colors`}
              >
                Outlined {variant}
              </button>
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Cards</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-bold mb-4">Standard Card</h3>
            <p className="text-gray-600">Basic card with white background</p>
          </div>

          <div className="relative rounded-lg shadow-md overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary opacity-90"></div>
            <div className="relative p-6">
              <h3 className="text-xl font-bold text-white mb-4">
                Gradient Card
              </h3>
              <p className="text-white/90">Card with gradient background</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-16">
        <h2 className="text-2xl font-bold mb-6">Form Elements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Text Input
              </label>
              <input
                type="text"
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                placeholder="Type something..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select
              </label>
              <select className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50">
                <option>Option 1</option>
                <option>Option 2</option>
                <option>Option 3</option>
              </select>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Textarea
              </label>
              <textarea
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                rows={3}
                placeholder="Type something longer..."
              />
            </div>
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-700">Checkbox</span>
              </label>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-2xl font-bold mb-6">Alerts</h2>
        <div className="space-y-4">
          {buttonVariants.map((variant) => (
            <div
              key={variant}
              className={`bg-${variant} bg-opacity-10 border-l-4 border-${variant} p-4 rounded`}
            >
              <p className={`text-${variant}`}>
                {variant.charAt(0).toUpperCase() + variant.slice(1)} Alert
                Message
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DesignSystem;
